/* eslint-disable @typescript-eslint/no-explicit-any */
import { cloneDeep } from "lodash";
import { uniqBy } from "lodash";
import type { KeyValueType } from "@/core/api/graphql/types";
import type { RouteLocationRaw } from "vue-router";

export function getBaseUrl(language: string, defaultLanguage: string): string {
  return language !== defaultLanguage ? `/${language}/` : "";
}

export function prefixLocaleUrl(
  url: string | undefined,
  locale: string,
  defaultLocale: string,
  german = "de",
): string | undefined {
  if (!url) {
    return url;
  }
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  // Algolia currently indexes URLs with a locale prefix already embedded (e.g. /en/catalog/...).
  // Strip any known locale prefix before applying the current one to avoid double-prefixing
  // (e.g. /zh/ + /en/catalog/... → /zh/en/catalog/... instead of the correct /zh/catalog/...).
  // TODO: remove this stripping once Algolia is re-indexed with locale-neutral URLs.
  const algoliaLocales = ["en", "zh", "ja", "de"];
  let normalizedUrl = url;
  for (const known of algoliaLocales) {
    if (url.startsWith(`/${known}/`)) {
      normalizedUrl = url.slice(known.length + 1); // e.g. "/en/foo" → "/foo"
      break;
    }
  }

  if (locale === defaultLocale || locale === german) {
    return normalizedUrl;
  }
  if (normalizedUrl.startsWith(`/${locale}/`)) {
    return normalizedUrl;
  }
  return `/${locale}${normalizedUrl}`;
}

export type LocalizeInternalUrlOptionsType = {
  /** Active app locale. */
  locale: string;
  /** Store default locale — its URL segment is never emitted. */
  defaultLocale: string;
  /** Every locale that can appear as a leading URL segment, used to strip a stale prefix. */
  knownLocales: string[];
  /** Locales served as a content overlay — they only ever keep a prefix the source URL already carried. */
  overlayLocales: string[];
  /** Hostnames that belong to this storefront (any `www.` prefix is ignored when matching). */
  sameSiteHostnames: string[];
  /** When set, only paths at or beneath this segment are rewritten; everything else is returned untouched. */
  pathFilter?: string;
};

const stripWww = (hostname: string): string => hostname.replace(/^www\./i, "").toLowerCase();

/**
 * Normalizes a same-site URL to the locale the visitor is actually browsing in.
 *
 * TODO: remove once every URL source emits locale-neutral paths. This is a defensive layer over
 * three sources that still embed a stale prefix: CMS `Url` properties synced before the
 * `CustomUrlConverter` fix (TL-4039), Algolia records indexed with `/en/...` (see `prefixLocaleUrl`),
 * and Commerce `softwarePageUrlTL` values (TL-3923). Once all three are re-synced/re-indexed and a
 * `/{defaultLocale}/*` → `/*` redirect is in place, call sites can bind the raw URL directly.
 */
export function localizeInternalUrl(url: string, options: LocalizeInternalUrlOptionsType): string {
  const { locale, defaultLocale, knownLocales, overlayLocales, sameSiteHostnames, pathFilter } = options;

  if (!url) {
    return url;
  }

  // Fragment-only (#tab), query-only (?x=1) and protocol-relative (//host/path) hrefs are not routes.
  if (url.startsWith("#") || url.startsWith("?") || url.startsWith("//")) {
    return url;
  }

  let path = url;

  if (/^https?:\/\//i.test(url)) {
    let parsedUrl: URL;
    try {
      parsedUrl = new URL(url);
    } catch {
      return url;
    }
    const hostname = stripWww(parsedUrl.hostname);
    if (!sameSiteHostnames.some((candidate) => stripWww(candidate) === hostname)) {
      return url;
    }
    path = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } else if (/^[a-z][a-z\d+.-]*:/i.test(url)) {
    // mailto:, tel:, javascript: … — never a site-relative route.
    return url;
  }

  if (!path.startsWith("/")) {
    path = `/${path}`;
  }

  const pathname = path.split(/[?#]/)[0];
  const leadingSegment = pathname.split("/")[1] ?? "";
  const hasLocalePrefix = knownLocales.includes(leadingSegment);

  if (pathFilter) {
    const neutralPathname = hasLocalePrefix ? pathname.slice(leadingSegment.length + 1) || "/" : pathname;
    if (neutralPathname !== pathFilter && !neutralPathname.startsWith(`${pathFilter}/`)) {
      return url;
    }
  }

  // An overlay prefix in the source URL is an intentional translated route. Keep it, but always
  // site-relative so a visitor on a ccTLD host is not bounced to another origin.
  if (overlayLocales.includes(leadingSegment)) {
    return path;
  }

  if (hasLocalePrefix) {
    const stripped = path.slice(leadingSegment.length + 1);
    // `/en?x=1` strips to `?x=1`, which is document-relative rather than site-relative.
    path = stripped.startsWith("/") ? stripped : `/${stripped}`;
  }

  if (locale === defaultLocale || overlayLocales.includes(locale)) {
    return path;
  }

  return path === "/" ? `/${locale}` : `/${locale}${path}`;
}

export function getReturnUrlValue(): string | null {
  const { searchParams } = new URL(location.href);
  let returnUrl = searchParams.get("returnUrl") || searchParams.get("ReturnUrl");
  if (searchParams.get("callbackAction") && searchParams.get("currencyCode")) {
    returnUrl += `?callbackAction=${searchParams.get("callbackAction")}&currencyCode=${searchParams.get("currencyCode")}`;
  }
  return returnUrl;
}

export function extractHostname(url: string) {
  let hostname = "";

  // Find & remove protocol (http, ftp, etc.) and get hostname
  if (url.indexOf("://") > -1) {
    hostname = url.split("/")[2];
  } else {
    hostname = url.split("/")[0];
  }

  // Find & remove port number
  hostname = hostname.split(":")[0];

  // Find & remove "?"
  hostname = hostname.split("?")[0];

  return hostname;
}

export function sleep(ms: number, resolvedValue?: any): Promise<any> {
  return new Promise((resolve) => setTimeout(resolve, ms, resolvedValue));
}

export function truncate(str: string, length: number): string {
  return str.length > length ? `${str.slice(0, length)}...` : str;
}

export function getLinkTarget(openInNewTab: boolean): "_blank" | "_self" {
  return openInNewTab ? "_blank" : "_self";
}

export function appendSuffixToFilename(filename: string, suffix: string, checkIfSuffixExists = false) {
  if (!filename) {
    return filename;
  }

  const dotIndex = filename.lastIndexOf(".");

  if (dotIndex === -1) {
    return checkIfSuffixExists && filename.endsWith(suffix) ? filename : filename + suffix;
  }

  const fileNameWithoutExtension = filename.substring(0, dotIndex);

  return checkIfSuffixExists && fileNameWithoutExtension.endsWith(suffix)
    ? filename
    : fileNameWithoutExtension + suffix + filename.substring(dotIndex);
}

//todo add unit test
export function stringFormat(template: string, ...args: string[]): string {
  return template.replace(/{(\d+)}/g, (match: string, num: number) => args[num] || match);
}

export function convertToType<To, From = Record<any, any>>(value?: From): To {
  return cloneDeep<To>(value as any);
}

export async function asyncForEach<T>(
  array: T[],
  callbackFn: (value: T, index: number, arr: T[]) => Promise<void>,
): Promise<void> {
  for (let i = 0, l = array.length; i < l; i++) {
    await callbackFn(array[i], i, array);
  }
}

export function extractNumberFromString(str: string): number {
  const regex = /\d+/;
  const match = regex.exec(str);
  return match ? parseInt(match[0]) : 0;
}

export function getValueByKey(data: KeyValueType[], key: string): string {
  const param = data.find((el) => el.key === key);

  if (!param?.value) {
    throw new Error(`Missed parameter ${key}`);
  }

  return param.value;
}

export function objectToKeyValues(object: { [key: string]: string }): KeyValueType[] {
  return Object.keys(object).map((key) => ({
    key,
    value: object[key],
  }));
}

export function replaceXFromBeginning(input: string, by: string = "•••• "): string {
  return input.replace(/^X+/, by);
}

type LinkAttrType = { to: RouteLocationRaw } | { externalLink: string } | object;

export const getLinkAttr = (link?: RouteLocationRaw): LinkAttrType => {
  if (typeof link === "string") {
    if (link.startsWith("/")) {
      return { to: link };
    } else {
      return { externalLink: link };
    }
  }
  return {};
};
export type UniqByLastIterateeType<T> = keyof T | ((item: T) => unknown);
export function uniqByLast<T>(arr: T[], iteratee: UniqByLastIterateeType<T>): T[] {
  return uniqBy(arr.slice().reverse(), iteratee).reverse();
}

/**
 * Cleans HTML entities and tags from text for use in browser tab titles.
 * - Replaces HTML entities like &nbsp; with regular spaces
 * - Strips HTML tags like <sup>, <sub>, etc. while preserving their content
 * @param text The text to clean
 * @returns Cleaned text suitable for browser tab titles
 */
export function cleanHtmlForTitle(text: string): string {
  if (!text) {
    return text;
  }

  // Create a temporary DOM element to decode HTML entities
  const tempElement = document.createElement("div");
  tempElement.innerHTML = text;

  // Get text content (this strips tags and decodes entities)
  return tempElement.textContent || tempElement.innerText || "";
}
